import React, { useState, useCallback, useMemo, useEffect, Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider, useRouter, RouterContext, getSemanticPath } from './contexts/RouterContext';
import Header from './components/Header';
import Footer from './components/Footer';
import { ThemeProvider } from './contexts/ThemeContext';
import ScrollToTopButton from './components/ScrollToTopButton';
import FloatingWhatsApp from './components/FloatingWhatsApp';
// Lazy load modals for bundle optimization
const ContactModal = lazy(() => import('./components/ContactModal'));
const LeadGenModal = lazy(() => import('./components/LeadGenModal'));
const GlobalSearchModal = lazy(() => import('./components/GlobalSearchModal'));
import { NavigationState } from './types';
import { getPartByCode } from './data/papers';
import { cmaFaqsData } from './data/cmaFaqs';
import LoadingSpinner from './components/LoadingSpinner';
import { SupabaseStatusProvider, useSupabaseStatus } from './contexts/SupabaseStatusContext';
import { connectionError } from './lib/supabaseClient';
import ConnectionStatusBanner from './components/ConnectionStatusBanner';
import SEO from './components/SEO';

// Lazy load pages for code-splitting and better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const CourseDetailsPage = lazy(() => import('./pages/CourseDetailsPage'));
const PracticalExperiencePage = lazy(() => import('./pages/PracticalExperiencePage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const CourseFeePage = lazy(() => import('./pages/CourseFeePage'));
const EnrollNowPage = lazy(() => import('./pages/EnrollNowPage'));
const PaperDetailPage = lazy(() => import('./pages/PaperDetailPage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const DipIFRSPage = lazy(() => import('./pages/DipIFRSPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const CmaBundleOfferPage = lazy(() => import('./pages/CmaBundleOfferPage'));
const DemoVideosPage = lazy(() => import('./pages/ResourcesPage'));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const OfficialResourcesPage = lazy(() => import('./pages/OfficialResourcesPage'));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'));
const CmaFaqsPage = lazy(() => import('./pages/CmaFaqsPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));

const PageRenderer: React.FC = () => {
  const { currentNav } = useRouter();

  switch (currentNav.page) {
    case 'home':
      return <HomePage />;
    case 'cma-course-details':
      return <CourseDetailsPage />;
    case 'cma-experience-requirement':
      return <PracticalExperiencePage />;
    case 'cma-course-fee':
      return <CourseFeePage />;
    case 'about':
      return <AboutUsPage />;
    case 'contact':
      return <ContactUsPage />;
    case 'enroll-now':
      return <EnrollNowPage />;
    case 'part-detail':
      return <PaperDetailPage paperCode={currentNav.params?.code} />;
    case 'testimonials':
      return <TestimonialsPage />;
    case 'privacy-policy':
      return <PrivacyPolicyPage />;
    case 'refund-policy':
      return <RefundPolicyPage />;
    case 'terms-and-conditions':
      return <TermsAndConditionsPage />;
    case 'dipifrs':
      return <DipIFRSPage />;
    case 'cma-bundle-offer':
      return <CmaBundleOfferPage />;
    case 'demo-videos':
      return <DemoVideosPage />;
    case 'articles':
        return <ArticlesPage />;
    case 'official-resources':
        return <OfficialResourcesPage />;
    case 'article-detail':
      return <ArticleDetailPage slug={currentNav.params?.slug} />;
    case 'cma-faqs':
      return <CmaFaqsPage />;
    case 'not-found':
    default:
      return <NotFoundPage />;
  }
};

const AppContent: React.FC = () => {
  const router = useRouter();
  const [isContactModalOpen, setContactModalOpen] = useState(false);
  const [isLeadGenModalOpen, setLeadGenModalOpen] = useState(false);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const { hasConnectionError } = useSupabaseStatus();

  const openContactModal = useCallback(() => setContactModalOpen(true), []);
  const closeContactModal = useCallback(() => setContactModalOpen(false), []);

  const openLeadGenModal = useCallback(() => {
    if (window.innerWidth < 768) return; // Do not show on mobile devices
    const hasSeenModal = sessionStorage.getItem('leadGenModalShown');
    if (!hasSeenModal) {
      setLeadGenModalOpen(true);
      sessionStorage.setItem('leadGenModalShown', 'true');
    }
  }, []);

  const closeLeadGenModal = useCallback(() => setLeadGenModalOpen(false), []);

  const openSearchModal = useCallback(() => setSearchModalOpen(true), []);
  const closeSearchModal = useCallback(() => setSearchModalOpen(false), []);

  // Override router context to handle 'contact' modal globally if needed,
  // but for SEO we navigate to the page. This is a good place to intercept nav actions.
  const routerContextValue = useMemo(() => ({
    currentNav: router.currentNav,
    navigateTo: router.navigateTo,
  }), [router.currentNav, router.navigateTo]);

  // --- SEO Management ---
  const getPageMetadata = (navState: NavigationState): { title: string, description: string, keywords: string, schema?: object | object[] } => {
    const baseTitle = 'Global Fin X';
    const separator = '|';
    
    switch (navState.page) {
        case 'home':
            return {
                title: "Best Online CMA US Training Global Fin X",
                description: "Join Global Fin X for the best online CMA US training. Get expert-led live classes, high pass rates, and dedicated support for CMA US certification globally.",
                keywords: "Online CMA US Training, Global CMA Classes, Best CMA Coaching Online, CMA US Certification, Global Fin X, CMA US Classes Online, CMA Institute",
                schema: [
                    {
                        "@context": "https://schema.org",
                        "@type": "EducationalOrganization",
                        "name": "Global Fin X",
                        "image": "https://res.cloudinary.com/dxohyfpnf/image/upload/f_auto,q_auto/v1760487060/GFX_New_Logo_nkguhn.webp",
                        "@id": "https://cma.globalfinx.in",
                        "url": "https://cma.globalfinx.in",
                        "telephone": "+918328080730",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Raithu Bidda Apartments, Simhapuri Colony, Bachupally",
                            "addressLocality": "Hyderabad",
                            "addressRegion": "Telangana",
                            "postalCode": "500090",
                            "addressCountry": "IN"
                        },
                        "sameAs": [
                            "https://www.facebook.com/GlobalFinX/",
                            "https://www.instagram.com/globalfinx/",
                            "https://www.linkedin.com/company/globalfinx"
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Course",
                        "name": "Online CMA US Training",
                        "description": "Comprehensive online CMA US training program. Get certified as a Certified Management Accountant with expert guidance globally.",
                        "provider": {
                            "@type": "EducationalOrganization",
                            "name": "Global Fin X",
                            "sameAs": "https://cma.globalfinx.in"
                        }
                    }
                ]
            };
        case 'cma-course-details':
            return {
                title: `CMA US Course Details & Syllabus | ${baseTitle}`,
                description: "Explore the complete CMA US course structure and syllabus. Detailed breakdown of Part 1 and Part 2, exam format, and eligibility for CMA US certification.",
                keywords: "CMA US course details, CMA US syllabus, CMA US exam structure, CMA US eligibility, CMA US parts, Financial Planning, Strategic Financial Management"
            };
        case 'cma-experience-requirement':
            return {
                title: `CMA US Experience Requirement Guide | ${baseTitle}`,
                description: "Understand the 2-year professional experience needed to become a Certified Management Accountant (CMA US). Learn about qualifying job roles globally.",
                keywords: "CMA US experience requirement, CMA US work experience, professional experience for CMA, management accounting jobs"
            };
        case 'cma-course-fee':
            return {
                title: `CMA US Course Fees & IMA Costs | ${baseTitle}`,
                description: "Get a detailed breakdown of CMA US course fees, including IMA membership, exam fees, and our discounted online coaching fees at Global Fin X.",
                keywords: "CMA US fees, CMA US cost, IMA fees, CMA US exam fee, CMA US online coaching fees"
            };
        case 'demo-videos':
            return {
                title: `CMA US Demo Videos & Free Classes | ${baseTitle}`,
                description: "Watch free demo videos and introductory classes for CMA US Part 1 and Part 2. Experience the best online CMA US training methodology at Global Fin X.",
                keywords: "CMA US demo videos, CMA US free classes, CMA US lectures, CMA Part 1 videos, CMA Part 2 videos"
            };
        case 'articles':
            return {
                title: `CMA US Articles & Study Guides | ${baseTitle}`,
                description: "Read in-depth articles, blogs, and study guides on CMA US preparation, global career opportunities, and management accounting topics.",
                keywords: "CMA US articles, CMA US blog, CMA US study guides, management accounting career, CMA US preparation"
            };
        case 'official-resources':
            return {
                title: `Official IMA & CMA US Resources | ${baseTitle}`,
                description: "Download official documents from IMA, including the CMA Candidate Handbook, Content Specification Outlines (CSOs), and Learning Outcome Statements (LOSs).",
                keywords: "CMA US handbook, CMA US syllabus PDF, CMA US LOS, CMA US CSO, IMA official documents"
            };
        case 'cma-faqs':
            return {
                title: `CMA US Exam FAQs | ${baseTitle}`,
                description: "Find answers to frequently asked questions about the CMA US exam, eligibility criteria, fees, scheduling, and the certification process.",
                keywords: "CMA US FAQ, CMA US questions, CMA US exam help, CMA US requirements",
                schema: {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": cmaFaqsData.flatMap(category => 
                        category.questions.map(faq => ({
                            "@type": "Question",
                            "name": faq.q,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": faq.a
                            }
                        }))
                    )
                }
            };
        case 'about':
            return {
                title: `About Global Fin X | Online CMA Institute`,
                description: "Learn about Global Fin X, a premier online CMA US Training Academy. Meet our expert faculty and discover our commitment to student success globally.",
                keywords: "About Global Fin X, online CMA US institute, CMA US tutors, global accounting academy",
                schema: {
                    "@context": "https://schema.org",
                    "@type": "AboutPage",
                    "name": "About Global Fin X",
                    "description": "Learn about Global Fin X, the premier online CMA US training institute. Meet our expert faculty and explore our journey.",
                    "publisher": {
                        "@type": "EducationalOrganization",
                        "name": "Global Fin X",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://res.cloudinary.com/dxohyfpnf/image/upload/f_auto,q_auto/v1760487060/GFX_New_Logo_nkguhn.webp"
                        }
                    }
                }
            };
        case 'contact':
            return {
                title: `Contact Us | ${baseTitle}`,
                description: "Get in touch with Global Fin X. Contact us for questions about CMA US online training, live classes, enrollment, or support.",
                keywords: "Contact Global Fin X, CMA US coaching contact, CMA US inquiry"
            };
        case 'enroll-now':
            return {
                title: `Enroll for CMA US Online Training | ${baseTitle}`,
                description: "Start your journey to becoming a Certified Management Accountant. Enroll today for expert-led CMA US online training at Global Fin X.",
                keywords: "Enroll CMA US, CMA US online classes, register for CMA US, CMA US courses online"
            };
        case 'testimonials':
            return {
                title: `CMA US Success Stories & Reviews | ${baseTitle}`,
                description: "Hear from our successful students globally. Read testimonials and reviews from those who have passed the CMA US exam with Global Fin X.",
                keywords: "CMA US testimonials, CMA US student reviews, Global Fin X reviews, CMA US success stories"
            };
        case 'cma-bundle-offer':
            return {
                title: `CMA US Both Parts Bundle Offer | ${baseTitle}`,
                description: "Get the best value with our CMA US bundle offer for both parts. This all-inclusive package provides everything you need to succeed at a discounted price.",
                keywords: "CMA US bundle offer, CMA US discount, CMA US both parts coaching, CMA US special offer"
            };
        case 'part-detail':
            const part = getPartByCode(navState.params?.code);
            if (part) {
                return {
                    title: `${part.title} Online Training | ${baseTitle}`,
                    description: `Deep dive into ${part.title} for CMA US. Explore the detailed syllabus, course features, and enroll for the best online coaching.`,
                    keywords: `CMA US ${part.code}, ${part.title}, CMA US course details, CMA US Part 1 coaching, CMA US Part 2 coaching`
                };
            }
            // Fallthrough for generic title
        case 'article-detail':
             return {
                title: `CMA US Article | ${baseTitle}`,
                description: "In-depth articles and insights on various CMA US topics to help you prepare for your exams and global career.",
                keywords: "CMA US article, management accounting, CMA US prep"
            };
        // Fallback for legal pages and others
        case 'privacy-policy': return { title: `Privacy Policy | ${baseTitle}`, description: "Read the privacy policy for Global Fin X.", keywords: "privacy policy, Global Fin X" };
        case 'refund-policy': return { title: `Refund Policy | ${baseTitle}`, description: "Read the refund policy for Global Fin X.", keywords: "refund policy, Global Fin X" };
        case 'terms-and-conditions': return { title: `Terms and Conditions | ${baseTitle}`, description: "Read the terms and conditions for Global Fin X.", keywords: "terms and conditions, Global Fin X" };
        default:
            return {
                title: `Page Not Found | ${baseTitle}`,
                description: "The page you are looking for could not be found.",
                keywords: "404, not found"
            };
    }
  };

  const getPageCanonicalUrl = (navState: NavigationState): string => {
    const baseUrl = 'https://cma.globalfinx.in';
    if (navState.page === 'not-found') return baseUrl;
    
    const pagePath = getSemanticPath(navState);
    let path = pagePath;
    
    // Normalize path by removing trailing slash (except for roof /)
    if (path.length > 1 && path.endsWith('/')) {
        path = path.slice(0, -1);
    }
    
    // For the home page, ensure we return the base URL without the trailing slash
    if (path === '/') return baseUrl;
    
    return `${baseUrl}${path}`;
  };




  // --- Event Handlers ---
  useEffect(() => {
    if (window.innerWidth < 768) {
      return; // Do not set up triggers on mobile devices
    }

    const timer = setTimeout(() => {
      openLeadGenModal();
    }, 15000); // Increased delay to 15s

    const handleMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        openLeadGenModal();
      }
    };

    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [openLeadGenModal]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            setSearchModalOpen(true);
        }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
        window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const errorMessage = useMemo(() => {
    if (connectionError) return connectionError;
    if (hasConnectionError) return "Could not connect to the database. Some features might be unavailable.";
    return "";
  }, [hasConnectionError]);

  return (
    <RouterContext.Provider value={routerContextValue}>
      <div className="bg-white dark:bg-black min-h-screen flex flex-col font-sans text-slate-700 dark:text-slate-300">
        <SEO 
          title={getPageMetadata(router.currentNav).title}
          description={getPageMetadata(router.currentNav).description}
          keywords={getPageMetadata(router.currentNav).keywords}
          canonicalUrl={getPageCanonicalUrl(router.currentNav)}
          noindex={router.currentNav.page === 'not-found'}
          schema={getPageMetadata(router.currentNav).schema}
        />
        <ConnectionStatusBanner message={errorMessage} />
        <Header onOpenSearch={openSearchModal} />
        <main className="flex-grow min-h-[80vh]">
          <Suspense fallback={<LoadingSpinner />}>
            <PageRenderer />
          </Suspense>
        </main>
        <Footer />
        <ScrollToTopButton />
        <FloatingWhatsApp />
        <Suspense fallback={null}>
            <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />
            <LeadGenModal isOpen={isLeadGenModalOpen} onClose={closeLeadGenModal} />
            <GlobalSearchModal isOpen={isSearchModalOpen} onClose={closeSearchModal} />
        </Suspense>
      </div>
    </RouterContext.Provider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <RouterProvider>
        <SupabaseStatusProvider>
          <AppContent />
        </SupabaseStatusProvider>
      </RouterProvider>
    </ThemeProvider>
  );
};

export default App;
